import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { SEOHead } from "@/components/SEOHead";
import { Calendar, CreditCard, MapPin, FileText, ChevronRight, ChevronLeft, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { Skeleton } from "@/components/ui/skeleton";
import { OrderStatusBadge } from "@/components/OrderStatusBadge";
import { EventImage } from "@/components/EventImage";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { getMyOrders } from "@/lib/api";
import { useNavigate, Link } from "react-router-dom";
import { cn, formatBRL } from "@/lib/utils";
import { normalizeText } from "@/lib/search";

const PAGE_SIZE = 10;

export default function MeusPedidos() {
  const { t } = useTranslation("buyer");
  const { user } = useAuth();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(1);

  const statusTabs = [
    { value: "all", label: t("orders.tabs.all") },
    { value: "paid", label: t("orders.tabs.paid") },
    { value: "pending", label: t("orders.tabs.pending") },
    { value: "cancelled", label: t("orders.tabs.cancelled") },
  ];

  // Realtime: auto-update when orders change
  useRealtimeSubscription({
    table: "orders",
    filter: user?.id ? `buyer_id=eq.${user.id}` : undefined,
    queryKey: ["my-orders", user?.id],
    enabled: !!user?.id,
  });

  const { data: orders, isLoading } = useQuery({
    queryKey: ["my-orders", user?.id],
    queryFn: () => getMyOrders(user!.id),
    enabled: !!user?.id,
  });

  const filteredOrders = useMemo(() => {
    if (!orders) return [];

    let result = orders as any[];

    // Filter by status
    if (statusFilter !== "all") {
      result = result.filter((order: any) => order.status === statusFilter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const normalizedQuery = normalizeText(searchQuery);
      result = result.filter((order: any) => {
        const eventTitle = order.events?.title || "";
        const orderId = order.id || "";
        return (
          normalizeText(eventTitle).includes(normalizedQuery) ||
          normalizeText(orderId).includes(normalizedQuery)
        );
      });
    }

    return result;
  }, [orders, statusFilter, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const paginatedOrders = filteredOrders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to first page when filters change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const paymentMethodLabel = (method: string | null) => {
    if (!method) return "—";
    switch (method) {
      case "credit_card": return t("orders.paymentMethods.credit_card");
      case "pix": return t("orders.paymentMethods.pix");
      case "boleto": return t("orders.paymentMethods.boleto");
      case "free": return t("orders.paymentMethods.free");
      default: return method;
    }
  };

  return (
    <>
      <SEOHead title={t("orders.seoTitle")} description={t("orders.seoDescription")} />
      <div className="container pt-4 lg:pt-24 pb-16">
        <div className="mb-8 space-y-1.5">
          <h1 className="font-display text-2xl lg:text-3xl font-bold text-foreground">{t("orders.title")}</h1>
          <p className="text-muted-foreground">{t("orders.subtitle")}</p>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
          </div>
        ) : !orders || orders.length === 0 ? (
          <EmptyState
            icon={<FileText className="h-12 w-12" />}
            title={t("orders.empty.title")}
            description={t("orders.empty.noOrdersDesc")}
            actionLabel={t("orders.empty.exploreAction")}
            onAction={() => navigate("/eventos")}
          />
        ) : (
          <>
            {/* Search and Filters */}
            <div className="space-y-4 mb-6">
              {/* Search input */}
              <div>
                <SearchInput
                  placeholder={t("orders.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Status filter tabs */}
              <div className="flex border-b border-border mb-6 overflow-x-auto no-scrollbar" role="tablist">
                {statusTabs.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => handleStatusChange(tab.value)}
                    className={cn(
                      "pb-3 px-1 mr-6 text-sm font-semibold transition-all border-b-2 whitespace-nowrap",
                      statusFilter === tab.value
                        ? "border-primary text-foreground"
                        : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Results */}
            {filteredOrders.length === 0 ? (
              <EmptyState
                icon={<Search className="h-12 w-12" />}
                title={t("orders.empty.title")}
                description={t("orders.empty.filterDesc")}
              />
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-3">
                  {t("orders.resultsCount", { count: filteredOrders.length })}
                </p>

                <div className="space-y-3">
                  {paginatedOrders.map((order: any) => {
                    const event = order.events;
                    return (
                      <Link
                        key={order.id}
                        to={`/pedido/${order.id}`}
                        className="flex items-start gap-4 p-4 rounded-2xl border border-border bg-card hover:border-primary/30 transition-colors"
                      >
                        {/* Event image */}
                        <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 hidden sm:block">
                          <EventImage
                            src={event?.cover_image_url}
                            alt={event?.title || "Evento"}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-display font-semibold text-foreground truncate">{event?.title || "Evento"}</h3>
                            <OrderStatusBadge status={order.payment_method === "boleto" && order.boleto_url ? "boleto_pending" : order.status} />
                          </div>

                          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1">
                              <Calendar className="h-3 w-3 text-primary" />
                              {new Date(order.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}
                            </span>
                            {order.payment_method && order.payment_method !== "free" && (
                              <span className="inline-flex items-center gap-1">
                                <CreditCard className="h-3 w-3 text-primary" />
                                {paymentMethodLabel(order.payment_method)}
                              </span>
                            )}
                            {event?.venue_city && (
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="h-3 w-3 text-primary" />
                                {event.venue_city}
                              </span>
                            )}
                          </div>

                          <p className="text-sm font-medium text-foreground">
                            {order.total === 0 ? t("orders.free") : formatBRL(order.total)}
                            {order.installments && order.installments > 1 && (
                              <span className="text-xs text-muted-foreground ml-1">
                                {t("orders.installments", { count: order.installments, amount: formatBRL(order.installment_value || order.total / order.installments) })}
                              </span>
                            )}
                          </p>

                          <p className="text-xs text-muted-foreground font-mono">
                            {t("orders.orderCode", { code: order.id.slice(0, 8).toUpperCase() })}
                          </p>
                        </div>

                        <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 mt-2" />
                      </Link>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      {t("orders.pagination.prev")}
                    </Button>

                    <span className="text-sm text-muted-foreground px-2">
                      {t("orders.pagination.pageInfo", { page, totalPages })}
                    </span>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      {t("orders.pagination.next")}
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
